"use client";

import routes from "@/app.route";
import useNumber from "@/hooks/number";
import useCart from "@/stores/cart";
import { ProductType, VariantType } from "@/types/product.type";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import Link from "next/link";
import config from "@/app.config";
import useUnavailableProduct from "@/stores/unavailable-product";
import SparkleEffect from "@/components/sparkle-effect";
import { getWithGstAmount } from "@/lib/gst";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useId, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import YoutubeSearchedForIcon from "@mui/icons-material/YoutubeSearchedFor";
import { StoreType } from "@/types/store.type";

export default function Summary({
  product,
  variant,
  stores,
  storeCode,
}: {
  product: ProductType;
  variant: VariantType;
  stores: StoreType[];
  storeCode: string;
}) {
  const { currency } = useNumber();
  const router = useRouter();
  const { loading, addItem } = useCart();
  const { setOpen } = useUnavailableProduct();
  const [openDialog, setOpenDialog] = useState(false);
  const [store, setStore] = useState(storeCode);
  const [show, setShow] = useState(
    stores.filter((v) => v.code == storeCode).length > 0,
  );
  const id = useId();
  const isInDraftCollection = Boolean(
    product.collections?.find((v) => v.handle == "drafts"),
  );

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const shareContent = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: window.document.title,
          text: product.title,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Share cancelled or failed", error);
      }
    } else {
      alert("Sharing is not supported in this browser");
    }
  };

  const calculateDeduction = (
    amount: string | number,
    intensity: string | number,
  ) => {
    const deductionSlabs = [
      { min: 300000, percentage: 2 },
      { min: 100000, percentage: 5 },
      { min: 0, percentage: 10 },
    ];
    const cleanAmount = Number(amount);

    const slab =
      deductionSlabs.find((s) => cleanAmount >= s.min) ??
      deductionSlabs[deductionSlabs.length - 1];

    const deductionAmount =
      (cleanAmount * (slab.percentage + Number(intensity))) / 100;

    return cleanAmount - deductionAmount;
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start gap-1">
          <h1 className="mb-2 md:text-3xl text-xl font-semibold">
            {product.title}
          </h1>
          <Tooltip title="Share">
            <IconButton onClick={shareContent}>
              <IosShareIcon />
            </IconButton>
          </Tooltip>
        </div>
        {Number(variant.price) > 0 && (
          <div className="flex items-center gap-2">
            <div className="mb-2 text-2xl font-semibold text-green-700">
              {currency(variant.price)}
            </div>
            <div className="flex items-center gap-2 text-sm">
              {variant.compare_at_price && (
                <div className="font-medium text-zinc-500 line-through">
                  {currency(
                    getWithGstAmount(
                      variant.compare_at_price,
                      variant.gst_rate,
                    ).toFixed(0),
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        {variant.taxable && (
          <Chip
            size="small"
            label={`${currency(getWithGstAmount(variant.price, variant.gst_rate).toFixed(0))} incl. GST — Claimable with B2B Billing.`}
          />
        )}
        {isInDraftCollection && (
          <>
            <div className="font-semibold mb-2 mt-3">Condition</div>
            <div className="space-y-3">
              <div
                className={`w-full text-left flex items-center justify-between gap-x-4 border-2 rounded-md px-4 h-[70px] ${Number(variant.available) == 0 || Number(variant.price) == 0 ? "cursor-no-drop border-dashed" : "border-blue-500 cursor-pointer"} `}
              >
                <div>
                  <div className="font-medium">Sealed New</div>
                  <div className="flex gap-2 items-center">
                    <div>{currency(variant.price)}</div>
                    <div className="size-1 rounded-full bg-zinc-500"></div>
                    {Number(variant.available) > 0 &&
                    Number(variant.price) > 0 ? (
                      <div className="text-green-600 text-sm">In Stock</div>
                    ) : (
                      <div className="text-rose-600 text-sm">Out of Stock</div>
                    )}
                  </div>
                </div>
                {(Number(variant.available) == 0 ||
                  Number(variant.price) == 0) && (
                  <Button
                    href={`tel:${config.contact.primaryPhone}`}
                    target="_blank"
                  >
                    Enquire Now
                  </Button>
                )}
              </div>
              <div
                className={`w-full text-left flex items-center justify-between gap-x-4 border-2 rounded-md px-4 h-[70px] cursor-no-drop border-dashed`}
              >
                <div>
                  <div className="font-medium">Open Box</div>
                  <div className="flex gap-2 items-center">
                    <div>{currency(calculateDeduction(variant.price, 1))}*</div>
                    <div className="size-1 rounded-full bg-zinc-500"></div>
                    <div className="text-rose-600 text-sm">Out of Stock</div>
                  </div>
                </div>
                <Button
                  href={`tel:${config.contact.primaryPhone}`}
                  target="_blank"
                >
                  Enquire Now
                </Button>
              </div>
              <div
                className={`w-full text-left flex items-center justify-between gap-x-4 border-2 rounded-md px-4 h-[70px] cursor-no-drop border-dashed`}
              >
                <div>
                  <div className="font-medium">Certified Pre-Owned</div>
                  <div className="flex gap-2 items-center">
                    <div>{currency(calculateDeduction(variant.price, 2))}*</div>
                    <div className="size-1 rounded-full bg-zinc-500"></div>
                    <div className="text-rose-600 text-sm">Out of Stock</div>
                  </div>
                </div>
                <Button
                  href={`tel:${config.contact.primaryPhone}`}
                  target="_blank"
                >
                  Enquire Now
                </Button>
              </div>
            </div>
          </>
        )}
        {!product.has_only_default_variant && (
          <>
            <div className="text-lg font-semibold mb-3 mt-6">Select Option</div>
            <div className="space-y-3">
              {product.variants.map((value, index) => (
                <Link
                  key={index}
                  href={`${routes.product.url.replace(":slug", product.handle)}?vid=${value.id}`}
                  className={`w-full text-left flex items-center gap-x-4 border-2 rounded-md px-6 h-[100px] ${variant.id == value.id ? "border-blue-500" : ""} ${Number(value.available) > 0 ? "cursor-pointer hover:border-blue-500" : ""} ${value.available == 0 ? "cursor-no-drop border-dashed pointer-events-none" : ""}`}
                >
                  <div>
                    <div className="font-medium">{value.title}</div>
                    <div>{currency(value.price)}</div>
                    {value.available == 0 && (
                      <div className="text-rose-600 text-sm">Out of Stock</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
        <div className="mt-4">
          <SparkleEffect>
            {Number(variant.available) > 0 ? (
              Number(variant.price) > 0 ? (
                <div className="flex gap-3 items-center">
                  <Button
                    className="md:w-1/2 w-full md:py-3.5! md:text-lg!"
                    loading={loading}
                    disabled={loading}
                    onClick={() => {
                      addItem({ ...variant, quantity: "1" });
                    }}
                    variant="outlined"
                  >
                    Add to Cart
                  </Button>
                  <Button
                    className="md:w-1/2 w-full md:py-3.5! md:text-lg!"
                    loading={loading}
                    disabled={loading}
                    onClick={() => {
                      addItem({ ...variant, quantity: "1" });
                      router.push(routes.checkout.url);
                    }}
                    variant="contained"
                  >
                    Buy Now
                  </Button>
                </div>
              ) : (
                <Button
                  href={`tel:${config.contact.primaryPhone}`}
                  target="_blank"
                  variant="outlined"
                >
                  Enquire Now
                </Button>
              )
            ) : (
              <Button
                onClick={() => setOpen(`${variant.id}`)}
                variant="outlined"
              >
                Notify Me
              </Button>
            )}
          </SparkleEffect>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <TextField
            select
            size="small"
            label="Select Store"
            onChange={(e) => {
              setStore(e.target.value);
              setShow(false);
            }}
            value={store}
            sx={{ minWidth: 250 }}
          >
            {stores.map(
              (value, index) =>
                value.code != null && (
                  <MenuItem key={index} value={value.code}>
                    {value.location}
                  </MenuItem>
                ),
            )}
          </TextField>
          <Button
            disabled={store.length == 0}
            startIcon={<YoutubeSearchedForIcon />}
            onClick={() => setShow(true)}
          >
            Check Availability
          </Button>
          {show && <Chip label="In Stock" color="success" />}
        </div>
        {product.summary && (
          <div
            className="bg-zinc-100 py-4 px-6 rounded mt-3 text-sm [&_ul]:space-y-1"
            dangerouslySetInnerHTML={{ __html: product.summary }}
          />
        )}
        {product.brochure && (
          <div className="mt-3">
            <Button
              target="_blank"
              startIcon={<DownloadRoundedIcon />}
              href={product.brochure.original_url}
              download={true}
            >
              Download Brochure
            </Button>
          </div>
        )}
        {isInDraftCollection && (
          <div className="mt-3">
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={handleClickOpen}>
                  View
                </Button>
              }
            >
              Ships globally. Within India, available only to authorised
              entities.
            </Alert>
          </div>
        )}
        <div className="mt-3">
          <div className="flex items-center gap-x-4">
            <Image
              src="/images/same-day-delivery.svg"
              alt="Same day dispatch, Order before 2:30 PM"
              width={40}
              height={40}
            />
            <p className="text-sm">Same day dispatch, Order before 2:30 PM</p>
          </div>

          <div className="flex items-center gap-x-4">
            <Image
              src="/images/easy-returns.svg"
              alt="30 days easy returns"
              width={40}
              height={40}
            />
            <p className="text-sm">
              30 days easy&nbsp;
              <Link href={routes.return.url}>returns</Link>
            </p>
          </div>

          <div className="flex items-center gap-x-4">
            <Image
              src="/images/24-hour-service.svg"
              alt="24 / 7 support service"
              width={40}
              height={40}
            />
            <p className="text-sm">24 / 7 support service</p>
          </div>

          <div className="flex items-center gap-x-4">
            <Image
              src="/images/cod.svg"
              alt="Cash on Delivery option available"
              width={40}
              height={40}
            />
            <p className="text-sm">Cash on Delivery option available</p>
          </div>

          <div className="flex items-center gap-x-4">
            <Image
              src="/images/1-year-warranty-on-software.svg"
              alt="1-Year Software Warranty"
              width={40}
              height={40}
            />
            <p className="text-sm">1-Year Software Warranty</p>
          </div>

          {/* <div className="flex items-center gap-x-4">
            <Image
              src="/images/book-your-drone.svg"
              alt="Reserve Your Drone for ₹5,000"
              width={40}
              height={40}
            />
            <p className="text-sm">Reserve Your Drone for ₹5,000</p>
          </div> */}
        </div>
      </div>

      <Dialog
        open={openDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        role="alertdialog"
      >
        <DialogTitle id="alert-dialog-title">
          Frequently Asked Questions
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Accordion elevation={0}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${id}-panel1-content`}
              id={`${id}-panel1-header`}
            >
              <Typography component="span">
                Who can purchase international drones?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                International drones can be purchased by both Indian and global
                customers. However, there are specific regulations based on
                location.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion elevation={0}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${id}-panel2-content`}
              id={`${id}-panel2-header`}
            >
              <Typography component="span">
                Are there any restrictions for Indian customers purchasing
                international drones?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Due to import regulations, only exempted entities can purchase
                international drones. These include:
              </Typography>
              <ul className="list-disc pl-5 mt-2">
                <li>Defence & Security organizations</li>
                <li>Research & Development institutions</li>
                <li>Government Entities</li>
                <li>Educational Institutions</li>
                <li>Others with proper government authorisation</li>
              </ul>
            </AccordionDetails>
          </Accordion>
          <Accordion elevation={0}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${id}-panel3-content`}
              id={`${id}-panel3-header`}
            >
              <Typography component="span">
                Are there any restrictions for global customers purchasing
                international drones?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                There are no restrictions; you can purchase international drones
                without verification.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
